'use client';

import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import { cn } from '@/ux/utils';

import { applyReadingTheme, getStoredTheme, readingThemes, storeTheme } from '../utils/readingThemes';

interface ReadingThemeSwitcherProps {
  onThemeChange?: (themeId: string) => void;
  className?: string;
  defaultTheme?: string;
}

export const ReadingThemeSwitcher: React.FC<ReadingThemeSwitcherProps> = ({
  onThemeChange,
  className,
  defaultTheme,
}) => {
  const [currentTheme, setCurrentTheme] = useState(defaultTheme || getStoredTheme());
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Apply initial theme
    const theme = readingThemes[currentTheme];
    if (theme) {
      applyReadingTheme(theme);
    }
  }, []);

  const handleThemeChange = (themeId: string) => {
    if (themeId === currentTheme || isTransitioning) return;

    setIsTransitioning(true);
    document.body.classList.add('theme-transitioning');

    // Apply new theme
    const theme = readingThemes[themeId];
    if (theme) {
      applyReadingTheme(theme);
      setCurrentTheme(themeId);
      storeTheme(themeId);
      onThemeChange?.(themeId);
    }

    // Remove transition class after animation
    setTimeout(() => {
      document.body.classList.remove('theme-transitioning');
      setIsTransitioning(false);
    }, 600);
  };

  const themes = Object.values(readingThemes);

  return (
    <motion.div
      className={cn('theme-switcher', className)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      onHoverStart={() => setIsExpanded(true)}
      onHoverEnd={() => setIsExpanded(false)}
      onTouchStart={() => setIsExpanded(true)}
      onTouchEnd={() => setTimeout(() => setIsExpanded(false), 2000)}
    >
      <AnimatePresence mode="popLayout">
        {themes.map((theme, index) => {
          const isActive = theme.id === currentTheme;
          const shouldShow = isExpanded || isActive;

          return shouldShow ? (
            <motion.button
              key={theme.id}
              className={cn('theme-option', isActive && 'active')}
              style={
                {
                  '--option-primary': theme.colors.primary,
                  '--option-secondary': theme.colors.secondary,
                } as React.CSSProperties
              }
              onClick={() => handleThemeChange(theme.id)}
              title={`${theme.name} - ${theme.description}`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{
                duration: 0.2,
                delay: isExpanded ? index * 0.05 : 0,
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="relative z-10">{theme.icon}</span>

              {/* Active indicator */}
              {isActive && (
                <motion.div
                  className="absolute inset-0 rounded-[14px]"
                  layoutId="activeTheme"
                  style={{
                    background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary})`,
                    opacity: 0.2,
                  }}
                  transition={{
                    type: 'spring',
                    stiffness: 300,
                    damping: 30,
                  }}
                />
              )}
            </motion.button>
          ) : null;
        })}
      </AnimatePresence>

      {/* Expanded state indicator */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className="absolute -top-12 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-black/80 text-white text-xs rounded-full whitespace-nowrap"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            {readingThemes[currentTheme]?.name} Mode
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
